import React from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { ArrowRight, Check, X, Minus } from "lucide-react";

const VersionCompare = ({ isOpen, onClose, previousData, currentData }) => {
  if (!previousData || !currentData) return null;

  const compareParticipants = () => {
    const changes = [];
    const prev = previousData.participants || [];
    const curr = currentData.participants || [];

    for (let i = 0; i < Math.max(prev.length, curr.length); i++) {
      const p = prev[i] || {};
      const c = curr[i] || {};
      
      if (p.name !== c.name || p.phone !== c.phone || p.type !== c.type) {
        changes.push({
          number: i + 1,
          prev: p,
          curr: c,
          changed: true
        });
      }
    }
    return changes;
  };

  const compareSequences = () => {
    const changes = [];
    const prev = previousData.sequences || [];
    const curr = currentData.sequences || [];

    for (let i = 0; i < Math.max(prev.length, curr.length); i++) {
      const p = prev[i] || {};
      const c = curr[i] || {};
      
      const pAssignments = (p.assignments || []).map(a => a.value).join(", ");
      const cAssignments = (c.assignments || []).map(a => a.value).join(", ");
      
      if (p.message_type !== c.message_type || pAssignments !== cAssignments) {
        changes.push({
          line: i + 1,
          prev: { ...p, assignments_str: pAssignments },
          curr: { ...c, assignments_str: cAssignments },
          changed: true
        });
      }
    }
    return changes;
  };

  const participantChanges = compareParticipants();
  const sequenceChanges = compareSequences();
  const hasChanges = participantChanges.length > 0 || sequenceChanges.length > 0;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-manrope flex items-center gap-2">
            Versionsvergleich
            <Badge variant="outline">V{previousData.version || 1} → V{(previousData.version || 1) + 1}</Badge>
          </DialogTitle>
        </DialogHeader>

        {!hasChanges ? (
          <div className="py-8 text-center text-slate-500">
            <Check className="w-12 h-12 text-emerald-500 mx-auto mb-2" />
            <p>Keine Änderungen zur vorherigen Version</p>
          </div>
        ) : (
          <div className="space-y-6 mt-4">
            {/* Participant Changes */}
            {participantChanges.length > 0 && (
              <div>
                <h3 className="font-semibold text-slate-900 mb-3">Teilnehmer-Änderungen</h3>
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead className="w-16">Nr.</TableHead>
                      <TableHead>Vorher</TableHead>
                      <TableHead className="w-10"></TableHead>
                      <TableHead>Nachher</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {participantChanges.map((change) => (
                      <TableRow key={change.number} className="bg-amber-50/50">
                        <TableCell className="font-mono">{change.number}</TableCell>
                        <TableCell>
                          <div className="text-sm">
                            <p className="font-medium text-red-600 line-through">
                              {change.prev.name || "(leer)"}
                            </p>
                            <p className="text-slate-500">
                              {change.prev.type} - {change.prev.country_code}{change.prev.phone}
                            </p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <ArrowRight className="w-4 h-4 text-slate-400" />
                        </TableCell>
                        <TableCell>
                          <div className="text-sm">
                            <p className="font-medium text-emerald-600">
                              {change.curr.name || "(leer)"}
                            </p>
                            <p className="text-slate-500">
                              {change.curr.type} - {change.curr.country_code}{change.curr.phone}
                            </p>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}

            {/* Sequence Changes */}
            {sequenceChanges.length > 0 && (
              <div>
                <h3 className="font-semibold text-slate-900 mb-3">Anwahlfolgen-Änderungen</h3>
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead className="w-16">Linie</TableHead>
                      <TableHead>Vorher</TableHead>
                      <TableHead className="w-10"></TableHead>
                      <TableHead>Nachher</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {sequenceChanges.map((change) => (
                      <TableRow key={change.line} className="bg-amber-50/50">
                        <TableCell className="font-mono">{change.line}</TableCell>
                        <TableCell>
                          <div className="text-sm">
                            <p className="font-medium text-red-600 line-through">
                              {change.prev.message_type || "(leer)"}
                            </p>
                            <p className="text-slate-500 text-xs">
                              {change.prev.assignments_str || "-"}
                            </p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <ArrowRight className="w-4 h-4 text-slate-400" />
                        </TableCell>
                        <TableCell>
                          <div className="text-sm">
                            <p className="font-medium text-emerald-600">
                              {change.curr.message_type || "(leer)"}
                            </p>
                            <p className="text-slate-500 text-xs">
                              {change.curr.assignments_str || "-"}
                            </p>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}

            {/* Customer Name Change */}
            {previousData.customer_name !== currentData.customer_name && (
              <div className="bg-slate-50 p-4 rounded-lg">
                <h3 className="font-semibold text-slate-900 mb-2">Kundenname</h3>
                <div className="flex items-center gap-2">
                  <span className="text-red-600 line-through">{previousData.customer_name}</span>
                  <ArrowRight className="w-4 h-4 text-slate-400" />
                  <span className="text-emerald-600 font-medium">{currentData.customer_name}</span>
                </div>
              </div>
            )}
          </div>
        )}

        <div className="flex justify-end pt-4 border-t mt-4">
          <Button variant="outline" onClick={onClose}>
            Schließen
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default VersionCompare;
